/**
 * whatsapp-button — points the floating WhatsApp link at the number for the market
 * the visitor is actually in.
 *
 * The country signal is the same one the localization prompt uses, and for the same
 * reason: this store has one Shopify market and one web presence shared by every
 * country, so `localization.country` is `BR` for a shopper in Dubai just as it is for
 * one in São Paulo. Liquid cannot tell them apart. Shopify's own GeoIP endpoint —
 * `/browsing_context_suggestions.json`, the one that used to back the Geolocation app
 * — can, needs no third-party service, no API key and no extra consent banner.
 * `detected_values.country.handle` is the visitor's resolved ISO country.
 *
 * This script only ever REWRITES an href that already works. Every failure path —
 * endpoint blocked, storefront password-protected, request timed out, no rule for the
 * country — leaves the server-rendered default number in place. There is no state in
 * which the button is present but dead.
 *
 * The resolved country is cached in sessionStorage for the tab, so browsing from the
 * homepage to a product to the cart costs one request rather than one per page.
 */

(() => {
  const CACHE_KEY = 'ghalya_geo_country';
  const REQUEST_TIMEOUT_MS = 4000;

  /**
   * Memoised per page load on top of the sessionStorage cache, so two elements on one
   * page (or a section re-render in the theme editor) cannot fan out into two requests.
   */
  let inflight = null;

  class WhatsAppButton extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      if (!this.config) return;

      this.link = this.querySelector('[data-link]');
      if (!this.link) return;

      // Nothing to resolve if the merchant has not written any country rules — the
      // default number is already correct for everyone, so skip the request entirely.
      if (!this.config.rules || Object.keys(this.config.rules).length === 0) return;

      this.route().catch(() => {});
    }

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-config]').textContent);
      } catch (_) {
        return null;
      }
    }

    async route() {
      const country = await this.detectCountry();
      if (!country) return;

      const number = this.config.rules[country.toUpperCase()];
      if (!number) return;

      this.link.href = `https://wa.me/${number}?text=${this.config.text}`;
      // Not read by anything here; it is the only way to tell from devtools whether
      // detection ran and what it decided.
      this.dataset.country = country.toUpperCase();
    }

    async detectCountry() {
      const cached = this.readCache();
      if (cached !== null) return cached;

      if (!inflight) inflight = this.fetchCountry();
      const country = await inflight;
      if (country) this.writeCache(country);
      return country;
    }

    /**
     * The visitor's ISO country per Shopify's edge, or null.
     *
     * While the storefront is password-protected this endpoint 302s to /password, which
     * answers 200 with HTML — so an `ok` response is not enough and the content type has
     * to be checked too. Failing closed here means the default number, which is the
     * right outcome.
     */
    async fetchCountry() {
      const url = `${this.config.root.replace(/\/$/, '')}/browsing_context_suggestions.json?${new URLSearchParams(
        { 'country[enabled]': 'true' }
      )}`;

      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(url, {
          signal: abort.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        if (!(response.headers.get('content-type') || '').includes('application/json')) return null;
        const payload = await response.json();
        const country = payload && payload.detected_values && payload.detected_values.country;
        return (country && country.handle) || null;
      } catch (_) {
        return null;
      } finally {
        clearTimeout(timer);
      }
    }

    readCache() {
      try {
        return window.sessionStorage.getItem(CACHE_KEY);
      } catch (_) {
        return null;
      }
    }

    writeCache(country) {
      try {
        window.sessionStorage.setItem(CACHE_KEY, country);
      } catch (_) {}
    }
  }

  if (!customElements.get('whatsapp-button')) {
    customElements.define('whatsapp-button', WhatsAppButton);
  }
})();
